import { DateTime } from 'luxon';
import { identity, range } from 'js/utils/ramda.util';
import { DAYS, PERIODS, FORMAT_TO_SAVE, DAY_PERIOD, TIME_FORMATS } from './constants';

export const getPeriod = (dateTime = '') => {
    const dailyFormat = /^\d{2}:\d{2}$/;
    const weeklyFormat = /^(MONDAY|TUESDAY|WEDNESDAY|THURSDAY|FRIDAY|SATURDAY|SUNDAY)\s\d{2}:\d{2}$/;

    if (dailyFormat.test(dateTime)) {
        return PERIODS.DAILY;
    } else if (weeklyFormat.test(dateTime)) {
        return PERIODS.WEEKLY;
    }

    return PERIODS.ONETIME;
};

export const capitalizeWord = (word) => word[0].toUpperCase() + word.toLowerCase().substring(1);

export const parseScheduledBreaksResponse = (response) => {
    const { timeSlotExclusions = [], revision = '' } = response?.data || {};
    const scheduledBreaks = timeSlotExclusions.map(item => {
        const { start, end, flags, effectiveEnd } = item;
        const period = getPeriod(start);

        return {
            ...item,
            period,
            isRevoked: Boolean(flags === 0 && effectiveEnd),
            end: period === PERIODS.WEEKLY ? capitalizeWord(end) : end,
            start: period === PERIODS.WEEKLY ? capitalizeWord(start) : start
        };
    });

    return { revision, scheduledBreaks };
};

export const getParsedDateTime = (time) => {
    const today = new Date();
    const [hours, minutes] = time.split(':');

    today.setHours(hours);
    today.setMinutes(minutes);

    return today;
};

export const days = DAYS.map(day => ({ value: day, label: day }));

export const hoursOptions = (timeFormat) => {
    const hoursBase = timeFormat === TIME_FORMATS.SHORT ? range(1, 13) : range(0, 24);

    return hoursBase.map(t => ({
        value: String(t).padStart(2, '0'),
        label: String(t).padStart(2, '0')
    }));
};

export const minutesOptions = [...Array(60).keys()].map(t => ({
    value: String(t).padStart(2, '0'),
    label: String(t).padStart(2, '0')
}));

export const formats = Object.keys(DAY_PERIOD).map(item => ({ value: item, label: item }));

export const intoDate = date => typeof date === 'number' ? new Date(date) : date;

export const getInitialValues = ({ formData, formatTime }) => {
    const { hours, minutes, day, date } = formData;
    const time = formatTime(`${hours}:${minutes}`);
    const [hour, secondPart] = time.split(':');
    const [minute, format] = secondPart.split(' ');

    return {
        hours: hour,
        minutes: minute,
        ...format && { format },
        ...day && { day },
        ...date && { date }
    };
};

export const getHoursToSubmit = (h, m, format) => {
    if (!format) {
        return h;
    }

    if (format === DAY_PERIOD.AM) {
        return `${Number(h) === 12 ? '00' : h}`;
    }

    return `${Number(h) === 12 ? '12' : Number(h) + 12}`;
};

export const getValuesPerPeriod = ({ data, period, modifyDate = identity, modifyDay = identity, modifyTime = identity }) => {
    const { start, end } = data[period];
    const startTime = modifyTime(`${start.hours}:${start.minutes}`);
    const endTime = modifyTime(`${end.hours}:${end.minutes}`);

    switch (period) {
        case PERIODS.DAILY: {
            return { start: startTime, end: endTime };
        }

        case PERIODS.WEEKLY: {
            return {
                start: `${modifyDay(start.day)} ${startTime}`,
                end: `${modifyDay(end.day)} ${endTime}`
            };
        }

        case PERIODS.ONETIME: {
            return {
                start: `${modifyDate(start.date)} ${startTime}`,
                end: `${modifyDate(end.date)} ${endTime}`
            };
        }

        default: break;
    }
};

export const getValuesToSave = ({ data, period }) =>
    getValuesPerPeriod({
        data,
        period,
        modifyDay: day => day.toUpperCase(),
        modifyDate: (date) => DateTime.fromJSDate(date).toFormat(FORMAT_TO_SAVE)
    });
